import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";
import { Button, Dimmer, DropdownItemProps, Grid, Header, Icon, Loader } from "semantic-ui-react";

import {
  getSupportedMimeType,
  URLS,
  useAnimationFrame,
  useInterval,
  usePlayerSettings,
  useSocialCoachSession,
} from "@/utils";
import { ControlSection } from "./components/ControlsSection";
import { RecordActionSection } from "./components/RecordActionsSection";

import { ScriptTextArea } from "@/styling/baseStyle";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { Media, VideoScript } from "@/services/bedrock";
import Webcam from "react-webcam";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { ConfirmationModal, VideoScriptSelectionDialog } from "@/components";
import { MediaServices } from "@/services/MediaServices";
import fixWebmDuration from "fix-webm-duration";
import { CoachSettings } from "@/interfaces";

interface RecordProps {
  title: string;
}

type Props = RouteComponentProps & {
  initialScript?: string;
  onVideoScriptSelected?: (script: VideoScript) => void;
  onCancel: () => void;
  onDone: (media: Media, secondsRecorded: number) => void;
  onSwitchToScreenCapture?: () => void;
};

const RecordVideoFC: React.FC<Props> = ({
  initialScript = "",
  location,
  onVideoScriptSelected,
  onCancel,
  onDone,
  onSwitchToScreenCapture,
}) => {
  const history = useHistory();
  const { isCoach, playerId } = useSocialCoachSession();
  const urls = isCoach ? URLS.coach : URLS.player;
  const { hasSetting } = usePlayerSettings(playerId);
  const hasScreenCapture = useMemo(() => {
    if (isCoach) return true;
    return hasSetting(CoachSettings.ALLOW_PLAYER_TO_SCREEN_CAPTURE, true);
  }, [hasSetting, isCoach]);

  const [recordingState, setRecordingState] = useState<"toStart" | "Review" | "Recording" | "Done">("toStart");
  const [speed, setSpeed] = useState<number>(15); // pixels per second
  const [textSize, setTextSize] = useState<number>(24);
  const [camera, setCamera] = useState<string | undefined>(undefined);
  const [confirmingCamera, setConfirmingCamera] = useState<string | undefined>(undefined);
  const [microphone, setMicrophone] = useState<string | undefined>(undefined);
  const [confirmingMicrophone, setConfirmingMicrophone] = useState<string | undefined>(undefined);
  const [cameraOptions, setCameraOptions] = useState<DropdownItemProps[]>([]);
  const [microphoneOptions, setMicrophoneOptions] = useState<DropdownItemProps[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [videoScriptModalOpen, setVideoScriptModalOpen] = React.useState(false);

  const preferredCameraId = localStorage.getItem("scRecorderPreferredCamera");
  const preferredMicId = localStorage.getItem("scRecorderPreferredMic");

  // Camera
  const webcamRef = useRef<Webcam>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [recorder, setRecorder] = useState<MediaRecorder | undefined>(undefined);
  const [mountingNewWebcam, setMountingNewWebcam] = useState(false);
  const [loadingWebcam, setLoadingWebcam] = useState(false);
  const chunksRef = useRef<Blob[]>([]);
  const [recordedBlob, setRecordedBlob] = useState<Blob | undefined>(undefined);
  const [dataReady, setDataReady] = useState(true);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const recordedDurationMs = useRef(0);
  const segmentStartTimeRef = useRef(0);
  const scrollAccumulator = useRef(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [viewSwitched, setViewSwitched] = useState(false);

  // Scroll
  const scrollToRef = useRef<HTMLTextAreaElement>(null);

  // Settings
  const [videoError, setVideoError] = useState<boolean>(false);
  const [withPermissions, setWithPermissions] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingLabel, setLoadingLabel] = useState<string>("Uploading");
  const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);
  const [aspectRatio, setAspectRatio] = useState(9 / 16);

  const [scriptMessage, setScriptMessage] = useState(initialScript);
  const [showScript, setShowScript] = useState(initialScript.length > 0);

  const isVertical = useMemo(() => {
    return aspectRatio == 9 / 16;
  }, [aspectRatio]);

  const isRecording = useMemo(() => {
    return recordingState === "Recording";
  }, [recordingState]);

  const showPreview = useMemo(() => {
    return previewUrl && previewUrl.length > 0 && !isRecording && dataReady && recordingState !== "toStart";
  }, [previewUrl, isRecording, dataReady, recordingState]);

  const hideWebcam = useMemo(() => {
    return !withPermissions || !camera || !microphone || showPreview;
  }, [camera, microphone, withPermissions, showPreview]);

  const showVideoLoader = useMemo(() => {
    return mountingNewWebcam || loadingWebcam || (hideWebcam && !showPreview && withPermissions !== false);
  }, [hideWebcam, showPreview, withPermissions, mountingNewWebcam, loadingWebcam]);

  useInterval(() => {
    if (isRecording) {
      const elapsedSinceSegmentStart = Date.now() - segmentStartTimeRef.current;
      const totalElapsedMs = recordedDurationMs.current + elapsedSinceSegmentStart;
      const secondsRecorded = Math.floor(totalElapsedMs / 1000);
      setDisplaySeconds(secondsRecorded);
    }
  }, 1000);

  useAnimationFrame(deltaTime => {
    const currentRef = scrollToRef?.current;
    if (isRecording && currentRef) {
      // Convert Pixels Per Second to Pixels Per Millisecond
      const pixelsPerMs = speed / 1000;

      // Calculate exactly how many fractional pixels we should have moved since the last frame
      const pixelsThisFrame = deltaTime * pixelsPerMs;

      scrollAccumulator.current += pixelsThisFrame;

      // Only fire a DOM update when we've built up at least 1 full pixel
      if (scrollAccumulator.current >= 1) {
        const pixelsToScroll = Math.floor(scrollAccumulator.current);
        currentRef.scrollTop += pixelsToScroll;
        scrollAccumulator.current -= pixelsToScroll; // Keep the remainder for the next frame!
      }
    }
  }, isRecording);

  useEffect(() => {
    const videoDevices = devices
      .filter(({ kind }) => kind === "videoinput")
      .map(element => {
        return {
          key: element.deviceId,
          text: element.label,
          value: element.deviceId,
        } as DropdownItemProps;
      });

    if (videoDevices.length > 0 && !camera) {
      const defaultCam = videoDevices.find(element => element.value === preferredCameraId) || videoDevices[0];
      setCamera(`${defaultCam.value}`);
    }

    setCameraOptions(videoDevices);

    const audios = devices
      .filter(({ kind }) => kind === "audioinput")
      .map(element => {
        return {
          key: element.deviceId,
          text: element.label,
          value: element.deviceId,
        } as DropdownItemProps;
      });

    setMicrophoneOptions(audios);
    if (audios.length > 0 && !microphone) {
      const defaultMic = audios.find(element => element.value === preferredMicId) || audios[0];
      setMicrophone(`${defaultMic.value}`);
    }
  }, [devices, setCamera, setMicrophone]);

  const refreshDevices = useCallback(() => {
    navigator.mediaDevices.enumerateDevices().then(setDevices);
  }, [setDevices]);

  const confirmStartOver = () => {
    setConfirmingMicrophone(undefined);
    setConfirmingCamera(undefined);
    setShowConfirmationModal(true);
  };

  useEffect(() => {
    if (!recorder) return;

    const handleDataAvailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    const handleStop = async () => {
      if (chunksRef.current.length > 0) {
        let blob = new Blob(chunksRef.current, { type: videoType });

        if (mimeType.includes("webm")) {
          blob = await fixWebmDuration(blob, recordedDurationMs.current);
        }

        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setRecordedBlob(blob);
        setDataReady(true); // Turns the UI back on
      }
    };

    recorder.addEventListener("dataavailable", handleDataAvailable);
    recorder.addEventListener("stop", handleStop);

    return () => {
      recorder.removeEventListener("dataavailable", handleDataAvailable);
      recorder.removeEventListener("stop", handleStop);
    };
  }, [recorder]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onStartOver = () => {
    // Clean up the media recorder to prevent late data chunks
    if (recorder) {
      if (recorder.state !== "inactive") {
        recorder.stop();
      }
    }

    // Reset standard React state
    setRecorder(undefined);
    setRecordingState("toStart");
    chunksRef.current = [];
    setRecordedBlob(undefined);
    setPreviewUrl(undefined);
    setDataReady(true);
    setDisplaySeconds(0);

    // Reset persistent refs (timers and scrolling)
    recordedDurationMs.current = 0;
    segmentStartTimeRef.current = 0;
    scrollAccumulator.current = 0;

    // Reset the teleprompter scroll position
    const currentRef = scrollToRef?.current;
    if (currentRef) {
      currentRef.scrollTop = 0;
    }
  };

  const newRecorder = () => {
    const stream = webcamRef?.current?.stream;
    if (!stream) return undefined;

    const newRecorder = new MediaRecorder(webcamRef?.current?.stream!!, {
      mimeType,
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 2500000,
    });
    setRecorder(newRecorder);
    return newRecorder;
  };

  const onStartRecording = () => {
    const stream = webcamRef?.current?.stream;

    if (!stream) {
      console.error("Attempted to start recording, but no stream is available.");
      return;
    }

    if (stream.getAudioTracks().length === 0) {
      const proceed = window.confirm(
        "We couldn't detect an active microphone. If you continue, your video will be silent. Do you want to record anyway?"
      );

      if (!proceed) return;
    }

    const recorderInstance = newRecorder();
    if (!recorderInstance) return;

    recordedDurationMs.current = 0;
    setDisplaySeconds(0);
    segmentStartTimeRef.current = Date.now();

    recorderInstance.start(1000);
    setRecordingState("Recording");
  };

  const onStopRecording = async () => {
    setDataReady(false);
    const segmentDuration = Date.now() - segmentStartTimeRef.current;
    recordedDurationMs.current += segmentDuration;

    recorder?.stop();
    setRecordingState("Review");
  };

  function RecordTitleSection({ title }: RecordProps) {
    return (
      <Grid.Row className="text-center m-0 p-0">
        <Header as="h2" className="w-full text-center text-[1.7em] font-semibold">
          {title}
        </Header>
      </Grid.Row>
    );
  }

  function VideoTitleSection({ title }: RecordProps) {
    return (
      <Grid.Row className="text-center m-0 p-0 block">
        <Header as="h2" className="w-[315px] text-center text-[1.7em] font-semibold">
          {title}
        </Header>
      </Grid.Row>
    );
  }

  const backAction = (): void => {
    if (isRecording) {
      if (window.confirm("Are you sure you want to discard this recording?")) {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const remountWebcam = () => {
    setMountingNewWebcam(true);
    setTimeout(() => {
      setMountingNewWebcam(false);
    }, 100);
  };

  const confirmCamera = (deviceId: string) => {
    if (deviceId) {
      if (recordingState !== "toStart") {
        setConfirmingCamera(deviceId);
        setShowConfirmationModal(true);
      } else {
        updateCamera(deviceId);
      }
    }
  };

  const updateCamera = (deviceId: string, restart: boolean = false) => {
    setConfirmingCamera(undefined);
    if (deviceId) {
      localStorage.setItem("scRecorderPreferredCamera", deviceId);
      if (restart) {
        onStartOver();
      } else {
        setRecorder(undefined);
        setCamera(deviceId);
        remountWebcam();
      }
    }
  };

  const confirmMic = (deviceId: string) => {
    if (deviceId) {
      if (recordingState !== "toStart") {
        setConfirmingMicrophone(deviceId);
        setShowConfirmationModal(true);
      } else {
        updateMic(deviceId);
      }
    }
  };

  const updateMic = (deviceId: string, restart: boolean = false) => {
    setConfirmingMicrophone(undefined);
    if (deviceId) {
      localStorage.setItem("scRecorderPreferredMic", deviceId);
      if (restart) {
        onStartOver();
      } else {
        setRecorder(undefined);
        setMicrophone(deviceId);
        remountWebcam();
      }
    }
  };

  const maxDeviceHeight: number | undefined = useMemo(() => {
    const device = devices.find(element => element.deviceId === camera);
    if (device && "getCapabilities" in device) {
      const capabilities = (device as unknown as MediaStreamTrack).getCapabilities();
      return capabilities?.height?.max;
    }

    return undefined;
  }, [devices, camera]);

  const resolutionWarning = useMemo(() => {
    const minHeight = 960;
    if (maxDeviceHeight && maxDeviceHeight < minHeight) {
      return `The selected camera has a resolution of ${maxDeviceHeight}px. The minimum resolution for most social networks is ${minHeight}px. Please consider using a camera with a higher resolution.`;
    }
    return undefined;
  }, [maxDeviceHeight]);

  const videoConstraints: MediaTrackConstraints | boolean = useMemo(() => {
    const baseConstraints = {
      facingMode: "user",
      frameRate: { max: 30, ideal: 30 },
      height: { ideal: isVertical ? 1920 : 1080 },
      aspectRatio: { exact: aspectRatio },
      resizeMode: "crop-and-scale",
    };

    if (!camera) return baseConstraints;

    return {
      ...baseConstraints,
      deviceId: { exact: camera },
    } as MediaTrackConstraints;
  }, [camera, isVertical, aspectRatio]);

  const audioConstraints: MediaTrackConstraints | boolean = useMemo(() => {
    if (!microphone) return true;
    return {
      deviceId: { exact: microphone },
    };
  }, [microphone]);

  const mimeType = getSupportedMimeType();
  let videoType = mimeType.split(";")[0];

  const onDoneRecording = useCallback(async () => {
    setRecordingState("Done");

    const uploader = new ProjectFileUploader("teleprompter");
    let ext = videoType.split("/")[1];
    setLoading(true);
    setLoadingProgress(0);
    setLoadingLabel("Uploading");

    const media: Media = {
      id: 0,
      sortOrder: 0,
      category: "VIDEO",
      fileName: `teleprompter_${new Date().getTime()}`,
      uri: previewUrl || "",
      type: videoType,
      extension: ext,
      imageFile: recordedBlob,
    };

    const uploaded = await uploader.prepareMedia([media], (_, progress) => {
      setLoadingProgress(Math.trunc(progress * 100));
    });

    let newMedia = uploaded[0];
    if (ext === "webm") {
      // TODO: do in the background while they make a choice
      newMedia = await transcode(newMedia);
    }
    setLoading(false);
    onDone(newMedia, displaySeconds);
  }, [displaySeconds, onDone, videoType, previewUrl, recordedBlob]);

  const transcode = async (media: Media) => {
    try {
      setLoadingProgress(undefined);
      setLoadingLabel("Processing media. This may take a minute...");
      let job = await MediaServices.transcode({ mediaUrl: media.uri });
      const jobId = job.jobName;
      while (job.jobStatus !== "SUCCEEDED") {
        await new Promise(r => setTimeout(r, 2000));
        job = await MediaServices.getJobStatus(jobId);
      }

      return {
        ...media,
        uri: job.downloadLink,
        extension: "mp4",
        type: "video/mp4",
      } as Media;
    } catch (e) {
      console.error("Error transcoding:", e);
      throw e;
    }
  };

  const previewPlayer = useMemo(() => {
    return (
      <ReactPlayer
        url={previewUrl}
        width={isVertical ? "auto" : "100%"}
        height={isVertical ? "560px" : "auto"}
        className="h-[560px] w-[315px] rounded-lg"
        controls
        playsinline
      />
    );
  }, [previewUrl, isVertical]);

  const ScriptSection = useMemo(() => {
    return (
      <Grid className="w-full pt-2.5 justify-center">
        <RecordTitleSection title="Script" />
        <Grid.Row stretched>
          {showScript && (
            <ScriptTextArea
              ref={scrollToRef}
              fontSize={textSize}
              key={"scriptText"}
              id={"scriptText"}
              value={scriptMessage}
              onChange={e => setScriptMessage(e.target.value)}
            />
          )}
          {!showScript && (
            <div className="flex flex-col w-full p-[30px] [&_.ui.button]:m-2.5">
              <Button secondary onClick={() => setVideoScriptModalOpen(true)}>
                Choose a script
              </Button>
              <Button secondary onClick={() => setShowScript(true)}>
                Write your own script
              </Button>
            </div>
          )}
        </Grid.Row>
      </Grid>
    );
  }, [showScript, textSize, scriptMessage]);

  const VideoSection = useMemo(() => {
    return (
      <Grid className="w-full pt-2.5 justify-center">
        <VideoTitleSection title={showPreview ? "Preview Video" : "Live Video"} />
        <Grid.Row>
          {videoError && (
            <Header
              as="h2"
              className="text-[#ff4136] mx-auto pt-[130px] px-5 pl-[30px] text-center h-[400px] w-[315px]"
            >
              {`The selected camera is not supported at this time. Please select a different camera. (details: ${maxDeviceHeight})`}
            </Header>
          )}
          {withPermissions === false && (
            <Header
              as="h2"
              className="text-[#ff4136] mx-auto pt-[130px] px-5 pl-[30px] text-center h-[400px] w-[315px]"
            >
              Please allow camera and microphone access to start recording
            </Header>
          )}
          <div
            className={
              "rounded-lg flex [&_video]:object-cover [&_video]:object-center [&_video]:overflow-auto " +
              (isVertical ? "w-[315px] h-[560px]" : "w-full")
            }
          >
            {showPreview && previewPlayer}
            {showVideoLoader && <Loader active={true}>Loading Camera Feed...</Loader>}
            {mountingNewWebcam ? undefined : (
              <Webcam
                key={`webcam-${camera}-${isVertical}`}
                className={hideWebcam ? "hidden" : "rounded-lg"}
                audio={true}
                muted={true}
                ref={webcamRef}
                disablePictureInPicture={true}
                mirrored={false}
                imageSmoothing={true}
                forceScreenshotSourceSize={true}
                screenshotFormat={"image/webp"}
                screenshotQuality={1}
                videoConstraints={videoConstraints}
                audioConstraints={audioConstraints}
                onUserMediaError={e => {
                  console.error("Error loading user media", e);
                  if (e.toString().includes("OverconstrainedError")) {
                    setVideoError(true);
                  } else if (e.toString().includes("NotAllowedError")) {
                    setWithPermissions(false);
                  }
                  setMountingNewWebcam(false);
                  setLoadingWebcam(false);
                }}
                onLoadStart={() => setLoadingWebcam(true)}
                onLoadedMetadata={() => setLoadingWebcam(false)}
                onUserMedia={() => {
                  setWithPermissions(true);
                  setMountingNewWebcam(false);
                  refreshDevices();
                }}
              />
            )}
          </div>
        </Grid.Row>
      </Grid>
    );
  }, [
    showPreview,
    videoError,
    maxDeviceHeight,
    withPermissions,
    isVertical,
    previewPlayer,
    showVideoLoader,
    mountingNewWebcam,
    camera,
    hideWebcam,
    videoConstraints,
    audioConstraints,
    refreshDevices,
  ]);

  return (
    <>
      <Grid>
        {/* Headers */}
        <Grid.Row centered>
          <Grid.Column width={4}>
            <Button secondary onClick={backAction}>
              Back
            </Button>
          </Grid.Column>

          <RecordActionSection
            recordingState={recordingState}
            disabled={!withPermissions || (!!hideWebcam && !showPreview) || loadingWebcam || mountingNewWebcam}
            doneDisabled={!dataReady}
            startHandler={onStartRecording}
            stopHandler={onStopRecording}
            doneHandler={onDoneRecording}
            startOverHandler={confirmStartOver}
            secondsRecorded={displaySeconds}
          />
        </Grid.Row>

        {/* Controls, script and video */}
        <Grid.Row centered>
          {/* Controls */}
          <Grid.Column width={4}>
            <Grid className="w-full pt-2.5 justify-center">
              <RecordTitleSection title="Controls" />
              <ControlSection
                onChangeSpeed={setSpeed}
                onChangeTextSize={setTextSize}
                onChangeCamera={confirmCamera}
                onChangeMicrophone={confirmMic}
                aspectRatio={aspectRatio}
                cameraOptions={cameraOptions}
                microphoneOptions={microphoneOptions}
                selectedCamera={camera}
                selectedMicrophone={microphone}
                disableCameraChanges={isRecording}
                canChangeAspectRatio={isRecording || displaySeconds > 0}
                onAspectRatioChange={setAspectRatio}
                warningMessage={resolutionWarning}
              />
            </Grid>
          </Grid.Column>

          {/* Script */}
          <Grid.Column width={6}>{viewSwitched ? VideoSection : ScriptSection}</Grid.Column>

          {/* Video */}
          <Grid.Column width={4}>
            <Icon
              name="arrows alternate horizontal"
              onClick={() => setViewSwitched(!viewSwitched)}
              className="absolute -top-1.5 -left-[50px]"
              disabled={isRecording}
              circular
            />
            {viewSwitched ? ScriptSection : VideoSection}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {hasScreenCapture !== false && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Button.Group size="medium">
            <Button active color="teal">
              <Icon name="file alternate" /> Teleprompter
            </Button>
            <Button
              disabled={isRecording}
              onClick={() => {
                if (
                  recordingState !== "toStart" &&
                  !window.confirm("Switching modes will clear your current progress. Are you sure?")
                ) {
                  return;
                }
                if (onSwitchToScreenCapture) {
                  onSwitchToScreenCapture();
                } else {
                  history.push(urls.screenCapture, location.state);
                }
              }}
            >
              <Icon name="desktop" /> Screen Record
            </Button>
          </Button.Group>
        </div>
      )}
      {loading && (
        <Dimmer active>
          <Loader indeterminate>
            {loadingLabel} {loadingProgress ? `${loadingProgress}%...` : ""}
          </Loader>
        </Dimmer>
      )}
      <ConfirmationModal
        title={"Warning! Are you sure?"}
        message={"This will restart the recording session, and lose all recorded progress."}
        openConfirmationModal={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        okHandler={() => {
          setShowConfirmationModal(false);
          if (confirmingCamera) {
            updateCamera(confirmingCamera, true);
          } else if (confirmingMicrophone) {
            updateMic(confirmingMicrophone, true);
          } else {
            onStartOver();
          }
        }}
        rejectHandler={() => {
          setShowConfirmationModal(false);
        }}
      />
      <VideoScriptSelectionDialog
        open={videoScriptModalOpen}
        onOpenChange={setVideoScriptModalOpen}
        title="Select a Video Script"
        description="Select a Video Script to use for your recording."
        onSelect={script => {
          setScriptMessage(script.content);
          setShowScript(true);
          setVideoScriptModalOpen(false);
          onVideoScriptSelected?.(script);
        }}
      />
    </>
  );
};

export const RecordVideo = withRouter(RecordVideoFC);
